'use client';
import { Box, Stack, Typography } from '@mui/material';
import Link from 'next/link';
import { Fragment } from 'react';
import { CENTERED_ICON, COLORS, MOTION, RADIUS, VISUALLY_HIDDEN } from '../../constants';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

export type ActionCardProps = {
  icon: MaterialIconType;
  title: string;
  /** Small grey text above the title, for label/value cards (e.g. "DNI" over the number) */
  label?: string;
  /** Grey text below the title */
  description?: string;
  /** Makes the whole card a link; internal paths use Next navigation */
  href?: string;
  /** Opens `href` in a new tab and shows the external link icon */
  external?: boolean;
  /** Replaces the default trailing icon (external link or chevron) */
  trailing?: React.ReactNode;
  /** Makes the whole card a button instead of a link, e.g. a choice that moves to the next step */
  onClick?: () => void;
  /** Brand outline, for the option picked before */
  selected?: boolean;
  /** Cuts the description after this many lines */
  descriptionLines?: number;
  /** Accessible name for the link when the visible text isn't enough */
  ariaLabel?: string;
  'data-testid'?: string;
};

const CARD_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  width: '100%',
  height: '100%',
  p: { xs: 2, md: 2.5 },
  borderRadius: `${RADIUS.xl}px`,
  // Same light grey as the operation detail tiles and mobile list cards, so every info tile on white matches
  bgcolor: COLORS.background[0],
  textAlign: 'left',
  textDecoration: 'none',
  boxSizing: 'border-box',
} as const;

const INTERACTIVE_SX = {
  cursor: 'pointer',
  transition: `transform ${MOTION.fast}ms ${MOTION.easeOut}, background-color ${MOTION.short}ms`,
  '&:hover': { bgcolor: COLORS.border.brand },
  '&:focus-visible': { outline: `2px solid ${COLORS.brand.main}`, outlineOffset: 2 },
  '&:active': { transform: `scale(${MOTION.cardPressScale})` },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } },
} as const;

/**
 * Filled light grey card with a round white icon chip, used for every list of links or values (profile data, help topics, contact
 * channels, marketing tools) so they all look the same. With `href` the whole card is one link, with `onClick` one button.
 */
export const ActionCard = ({
  icon,
  title,
  label,
  description,
  href,
  external,
  trailing,
  onClick,
  selected,
  descriptionLines,
  ariaLabel,
  'data-testid': testId = 'actionCard',
}: ActionCardProps) => {
  const defaultTrailing =
    href || onClick ? (
      <MaterialIcon
        icon={external ? 'open_in_new' : 'chevron_right'}
        size={20}
        aria-hidden="true"
        sx={{ color: COLORS.text.brand }}
      />
    ) : null;
  const content = (
    <>
      <Box
        aria-hidden="true"
        sx={{
          ...CENTERED_ICON,
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: COLORS.surface.raised,
        }}
      >
        <MaterialIcon icon={icon} size={22} sx={{ color: COLORS.text.brand }} />
      </Box>
      <Stack sx={{ gap: 0.25, flex: 1, minWidth: 0 }}>
        {label && (
          <Typography component="span" variant="body2" sx={{ color: COLORS.text.light }}>
            {label}
          </Typography>
        )}
        {/* Long values (emails) break onto a second line on narrow phones instead of being cut off, preferably after
         the @ or a dot */}
        <Typography
          component="span"
          sx={{
            color: COLORS.text.main,
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            overflowWrap: 'anywhere',
          }}
        >
          {title.split(/(?<=[@.])/).map((part, index) => (
            <Fragment key={index}>
              {index > 0 && <wbr />}
              {part}
            </Fragment>
          ))}
        </Typography>
        {description && (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: COLORS.text.light,
              ...(descriptionLines && {
                display: '-webkit-box',
                WebkitLineClamp: descriptionLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            }}
          >
            {description}
          </Typography>
        )}
      </Stack>
      {trailing ?? defaultTrailing}
      {external && <Box sx={VISUALLY_HIDDEN}>(se abre en una pestaña nueva)</Box>}
    </>
  );

  const selectedSx = selected ? { boxShadow: `inset 0 0 0 1.5px ${COLORS.brand.main}` } : {};
  if (onClick) {
    return (
      <Box
        data-testid={testId}
        component="button"
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-current={selected ? 'true' : undefined}
        sx={{ ...CARD_SX, ...INTERACTIVE_SX, ...selectedSx, border: 'none', font: 'inherit' }}
      >
        {content}
      </Box>
    );
  }
  if (!href) {
    return (
      <Box data-testid={testId} sx={CARD_SX}>
        {content}
      </Box>
    );
  }
  const internal = !external && href.startsWith('/');
  return (
    <Box
      data-testid={testId}
      component={internal ? Link : 'a'}
      href={href}
      aria-label={ariaLabel}
      {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      sx={{ ...CARD_SX, ...INTERACTIVE_SX }}
    >
      {content}
    </Box>
  );
};
